import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { TextField, Grid, Box, MenuItem, Typography } from "@material-ui/core";
import styled from "styled-components";
import Dialog from "../../../waybee-ui/Dialog";
import DialogTitle from "../../../waybee-ui/Dialog/DialogTitle";
import DialogContent from "../../../waybee-ui/Dialog/DialogContent";
import DialogActions from "../../../waybee-ui/Dialog/DialogActions";
import Button from "../../../waybee-ui/Button";
import { Can } from "../../../acl/can";

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SectionTitle = styled(Typography)`
  && {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #455a64;
  }
`;

const toDateInput = value => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  return "";
};

const toDateTimeInput = value => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) return str.slice(0, 16);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return `${str.slice(0, 10)}T00:00`;
  return "";
};

const initialComplValues = (fields, provaCompl) => {
  const values = {};
  (fields || []).forEach(field => {
    const current = provaCompl?.[field.name];
    if (current != null && current !== "") {
      values[field.name] = String(current);
    } else if (field.defaultValue) {
      values[field.name] = String(field.defaultValue);
    } else {
      values[field.name] = "";
    }
  });
  return values;
};

const isBlank = value => String(value ?? "").trim() === "";

const TestDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  isEditing,
  evaluation,
  complementaryFields
}) => {
  const { t } = useTranslation();
  const requiredMessage = t("grades.test.required");
  const prova = evaluation.SProvas || {};
  const provaCompl = evaluation.SProvasCompl || {};
  const fields = complementaryFields || [];

  const [description, setDescription] = useState(
    isEditing && prova.DESCRICAO ? prova.DESCRICAO : ""
  );
  const [value, setValue] = useState(
    isEditing && prova.VALOR != null && prova.VALOR !== ""
      ? String(prova.VALOR)
      : ""
  );
  const [average, setAverage] = useState(
    isEditing && prova.MEDIA != null && prova.MEDIA !== ""
      ? String(prova.MEDIA)
      : ""
  );
  const [plannedDate, setPlannedDate] = useState(
    isEditing ? toDateInput(prova.DTPREVISTA) : ""
  );
  const [testDate, setTestDate] = useState(
    isEditing ? toDateInput(prova.DTPROVA) : ""
  );
  const [deliveryLimit, setDeliveryLimit] = useState(
    isEditing ? toDateTimeInput(prova.DTLIMITEENTREGAAVAL) : ""
  );
  const [complValues, setComplValues] = useState(
    initialComplValues(fields, provaCompl)
  );
  const [errors, setErrors] = useState({});

  const clearFieldError = fieldName => {
    setErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const setComplField = (name, next) => {
    setComplValues(prev => ({ ...prev, [name]: next }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (isBlank(description)) nextErrors.description = requiredMessage;
    if (isBlank(value)) nextErrors.value = requiredMessage;
    if (isBlank(average)) nextErrors.average = requiredMessage;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = () => {
    if (!validateForm()) return;

    const complPayload = {};
    fields.forEach(field => {
      if (!field.editable) return;
      const raw = complValues[field.name];
      if (raw === "" || raw == null) return;
      complPayload[`SPROVASCOMPL.${field.name}`] = raw;
    });

    onSave({
      ...(isEditing ? prova : {}),
      DESCRICAO: description.trim(),
      VALOR: value === "" ? null : value,
      MEDIA: average === "" ? null : average,
      DTPREVISTA: plannedDate || null,
      DTPROVA: testDate || null,
      DTLIMITEENTREGAAVAL: deliveryLimit || null,
      CODETAPA: prova.CODETAPA || undefined,
      ...complPayload
    });
  };

  const renderComplField = field => {
    const fieldValue = complValues[field.name] ?? "";
    const common = {
      key: field.name,
      label: field.title || field.name,
      value: fieldValue,
      onChange: e => setComplField(field.name, e.target.value),
      fullWidth: true,
      disabled: !field.editable,
      InputLabelProps: field.type === "D" ? { shrink: true } : undefined
    };

    if (field.hasLookup && field.options?.length) {
      return (
        <Grid item xs={10} key={field.name}>
          <TextField {...common} select>
            <MenuItem value="">
              <em>{t("common.actions.select")}</em>
            </MenuItem>
            {field.options.map(opt => (
              <MenuItem key={opt.id} value={opt.id}>
                {field.lookupByCode === false
                  ? opt.label
                  : `${opt.id} — ${opt.label}`}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    if (field.type === "D") {
      return (
        <Grid item xs={10} key={field.name}>
          <TextField {...common} type="date" />
        </Grid>
      );
    }

    if (field.type === "N") {
      return (
        <Grid item xs={10} key={field.name}>
          <TextField
            {...common}
            type="number"
            inputProps={{
              step: field.decimalLength ? "0.01" : "1"
            }}
          />
        </Grid>
      );
    }

    if (field.type === "B") {
      return (
        <Grid item xs={10} key={field.name}>
          <TextField {...common} select>
            <MenuItem value="">
              <em>{t("common.actions.select")}</em>
            </MenuItem>
            <MenuItem value="S">{t("common.actions.yes")}</MenuItem>
            <MenuItem value="N">{t("common.actions.no")}</MenuItem>
          </TextField>
        </Grid>
      );
    }

    return (
      <Grid item xs={10} key={field.name}>
        <TextField
          {...common}
          type="text"
          inputProps={{
            maxLength: field.length ? Number(field.length) : undefined
          }}
        />
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth closeButton>
      <div>
        <DialogTitle>
          {!isEditing ? t("grades.v2.newEval") : t("grades.v2.editEval")}
        </DialogTitle>

        <DialogContent>
          {!isEditing && t("grades.test.help")}
          <DialogContainer>
            <Box mt={3} width="100%">
              <Grid item xs={10}>
                <TextField
                  label={t("common.labels.description")}
                  value={description}
                  onChange={e => {
                    setDescription(e.target.value);
                    clearFieldError("description");
                  }}
                  type="text"
                  fullWidth
                  required
                  error={Boolean(errors.description)}
                  helperText={errors.description || ""}
                />
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label={t("grades.test.value")}
                  value={value}
                  onChange={e => {
                    setValue(e.target.value);
                    clearFieldError("value");
                  }}
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  fullWidth
                  required
                  error={Boolean(errors.value)}
                  helperText={errors.value || ""}
                />
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label={t("grades.test.average")}
                  value={average}
                  onChange={e => {
                    setAverage(e.target.value);
                    clearFieldError("average");
                  }}
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  fullWidth
                  required
                  error={Boolean(errors.average)}
                  helperText={errors.average || ""}
                />
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label={t("grades.test.plannedDate")}
                  value={plannedDate}
                  onChange={e => setPlannedDate(e.target.value)}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label={t("grades.test.evalDate")}
                  value={testDate}
                  onChange={e => setTestDate(e.target.value)}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={10}>
                <TextField
                  label={t("grades.test.deliveryLimit")}
                  value={deliveryLimit}
                  onChange={e => setDeliveryLimit(e.target.value)}
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Box>

            {fields.length > 0 && (
              <Box mt={1} width="100%">
                <SectionTitle variant="subtitle1">
                  {t("grades.test.complementaryFields")}
                </SectionTitle>
                {fields.map(renderComplField)}
              </Box>
            )}
          </DialogContainer>
        </DialogContent>
      </div>

      {!isEditing ? (
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={onClose}>
            {t("common.actions.back")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            type="submit"
          >
            {t("common.actions.add")}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            {t("common.actions.edit")}
          </Button>
          <Can I="delete" a="scoresTest">
            <Button
              variant="contained"
              color="primary"
              onClick={onDelete}
              type="submit"
            >
              {t("common.actions.delete")}
            </Button>
          </Can>
        </DialogActions>
      )}
    </Dialog>
  );
};

TestDialog.defaultProps = {
  open: false,
  isEditing: false,
  onSave: () => {},
  onDelete: () => {},
  evaluation: {},
  complementaryFields: []
};

TestDialog.propTypes = {
  open: PropTypes.bool,
  isEditing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  evaluation: PropTypes.shape({
    SProvas: PropTypes.shape({}),
    SProvasCompl: PropTypes.shape({})
  }),
  complementaryFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      type: PropTypes.string
    })
  )
};

export default TestDialog;
